import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ModalHeader from 'reactstrap/lib/ModalHeader';
import Button from 'reactstrap/lib/Button';
import ModalBody from 'reactstrap/lib/ModalBody';
import Modal from 'reactstrap/lib/Modal';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { VehicleEntities } from 'client/data/models/vehicle';
import { getFullImageUrl } from 'client/utils/image-helpers';
import { IMAGE_SIZES } from 'site-modules/shared/constants/editorial/editorial';
import { CorePageParams } from 'site-modules/shared/utils/core-page/params';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { VehiclePhoto } from 'site-modules/shared/components/vehicle-photo/vehicle-photo';
import { EditorsChoiceBadge } from 'site-modules/shared/components/editors-choice-badge/editors-choice-badge';
import { HeroCarousel } from 'site-modules/shared/components/hero-carousel/hero-carousel';
import { Video } from 'site-modules/shared/components/video';

import './photoflipper-image.scss';

export function PhotoflipperImage({
  params,
  vehicle,
  media = null,
  iconClassName = null,
  hasEditorialChoiceBadge = false,
  isPreprod = false,
  isMobile = false,
  disableTrademarkCharacter = false,
}) {
  const [isPhotoflipperOpen, togglePhotoflipper] = useToggle();

  const carouselSlides =
    media &&
    media.map(({ href, title, rel }, index) => {
      const key = `${href}-${index}`;
      if (rel === 'video_youtube') {
        return (
          <div key={key} className="video-slide" data-index={index}>
            <Video containerClassName="video" videoId={href} type="Youtube" />
          </div>
        );
      }

      const imageUri = getFullImageUrl(href, IMAGE_SIZES.lg);
      return (
        <div key={key} className="slide" data-index={index}>
          <img className="photo my-auto" alt={title} src={imageUri} />
        </div>
      );
    });

  const vehicleName = buildDisplayVehicle({
    makeModelSubmodelYear: vehicle,
    params,
    trimDuplicateSubmodel: true,
    usedPrefix: '',
    disableTrademarkCharacter,
  });

  return (
    <div className="pos-r photoflipper-image-container mb-0_25 rounded">
      {isPreprod ? (
        <Fragment>
          <HeroCarousel
            autoplay={false}
            lazyLoad
            className="editorial-hero-carousel mb-0_5"
            data-tracking-parent="edm-entry-editorial-hero-carousel"
            slides={carouselSlides}
          />
          {!isMobile && (
            <Fragment>
              <Button
                color="link"
                className="photo-link-btn size-16 p-0 text-primary-darker text-capitalize border-0"
                onClick={togglePhotoflipper}
              >
                Photos & Videos
              </Button>

              <Modal
                className="photoflipper-image-carousel-modal"
                isOpen={isPhotoflipperOpen}
                toggle={togglePhotoflipper}
                centered
                size="lg"
              >
                <ModalHeader tag="div" className="justify-content-end p-0" onClick={togglePhotoflipper}>
                  <Button color="link" className="text-decoration-none p-0_25 heading-3 text-white">
                    <i className="icon-cross2" aria-hidden />
                  </Button>
                </ModalHeader>
                <ModalBody className="p-0">
                  <HeroCarousel
                    autoplay={false}
                    lazyLoad
                    className="editorial-hero-carousel"
                    data-tracking-parent="edm-entry-editorial-hero-carousel"
                    slides={carouselSlides}
                  />
                </ModalBody>
              </Modal>
            </Fragment>
          )}
        </Fragment>
      ) : (
        <VehiclePhoto
          params={params}
          vehicle={vehicle}
          iconClassNames={
            hasEditorialChoiceBadge
              ? null
              : classnames('icon-camera2 rounded text-primary-darker p-0_5 btn btn-lg', iconClassName)
          }
          dataTrackingId="open_photoflipper"
          altText={vehicleName}
          allocateSpace
        />
      )}

      {hasEditorialChoiceBadge && <EditorsChoiceBadge vehicleParams={params} className="m-0_5 m-md-1" />}
    </div>
  );
}

PhotoflipperImage.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  media: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string,
      rel: PropTypes.string,
    })
  ),
  iconClassName: PropTypes.string,
  hasEditorialChoiceBadge: PropTypes.bool,
  isPreprod: PropTypes.bool,
  isMobile: PropTypes.bool,
  disableTrademarkCharacter: PropTypes.bool,
};
